/**
 * MqttManager.ts
 * MQTT连接管理器
 * 
 * 功能:
 * - 管理MQTT连接
 * - 提供主题订阅和发布
 * - 统一错误处理和重连逻辑
 * - 支持JWT认证
 */

// Buffer polyfill for browser environment
import { Buffer } from 'buffer';
import mqtt, { MqttClient, IClientOptions } from 'mqtt';
import { logDeviceStatusMqttRawFromBroker } from '../../utils/deviceStatusMqttDebug';

// @ts-ignore
global.Buffer = Buffer;

// MQTT连接配置
export interface MqttConnectionConfig {
  brokerUrl: string;
  clientId: string;
  username?: string;
  password?: string;
  keepalive?: number;
  reconnectPeriod?: number;
  connectTimeout?: number;
  clean?: boolean;
  qos?: 0 | 1 | 2;
}

// MQTT连接状态
export interface MqttConnectionStatus {
  isConnected: boolean;
  isConnecting: boolean;
  lastConnectedTime: number;
  lastDisconnectedTime: number;
  reconnectAttempts: number;
  latency: number;
  brokerUrl: string;
  clientId: string;
}

// MQTT消息
export interface MqttMessage {
  topic: string;
  payload: string | Buffer;
  qos: 0 | 1 | 2;
  retain: boolean;
  timestamp: number;
}

// MQTT事件回调
export interface MqttEventCallbacks {
  onConnect?: () => void;
  onDisconnect?: (reason?: string) => void;
  onMessage?: (topic: string, message: string | Buffer) => void;
  onError?: (error: Error) => void;
  onReconnect?: () => void;
  onOffline?: () => void;
}

/**
 * MQTT连接管理器
 */
export class MqttManager {
  private client: MqttClient | null = null;
  private config: MqttConnectionConfig;
  private status: MqttConnectionStatus;
  private callbacks: MqttEventCallbacks = {};
  private messageListeners = new Set<(topic: string, message: string | Buffer) => void>();
  private subscriptions: Map<string, number> = new Map(); // topic -> qos
  private isConnecting = false;
  private connectionPromise: Promise<void> | null = null;

  constructor(config: MqttConnectionConfig) {
    this.config = {
      keepalive: 60,
      reconnectPeriod: 5000,
      connectTimeout: 30000,
      clean: true,
      qos: 1,
      ...config
    };

    this.status = {
      isConnected: false,
      isConnecting: false,
      lastConnectedTime: 0,
      lastDisconnectedTime: 0,
      reconnectAttempts: 0,
      latency: 0,
      brokerUrl: config.brokerUrl,
      clientId: config.clientId
    };

  }

  /**
   * 从后台回到前台或网络恢复时调用。
   * iOS 上各类浏览器（含 Chrome）均为 WebKit：后台会节流定时器，MQTT keepalive 易迟到，Broker 断开；
   * 也可能出现本地仍显示已连接但 WebSocket 已关闭的半开状态。
   */
  async refreshConnectionAfterBackground(): Promise<void> {
    if (this.isConnecting) {
      return;
    }

    const client = this.client as any;
    if (!client) {
      await this.connect();
      return;
    }

    let ws: WebSocket | undefined;
    try {
      const stream = client.stream;
      if (stream?.socket && typeof stream.socket.readyState === 'number') {
        ws = stream.socket as WebSocket;
      } else if (stream && typeof (stream as WebSocket).readyState === 'number') {
        ws = stream as WebSocket;
      }
    } catch {
      /* mqtt.js 结构因版本/打包可能略有差异 */
    }

    const wsDead =
      typeof WebSocket !== 'undefined' &&
      ws != null &&
      ws.readyState !== WebSocket.OPEN;

    if (wsDead) {
      console.warn('MqttManager: 检测到 WebSocket 已非 OPEN，前台恢复后重建连接');
      try {
        client.end(true);
      } catch {
        /* ignore */
      }
      this.client = null;
      this.status.isConnected = false;
      await this.connect();
      return;
    }

    if (!this.isConnected()) {
      await this.connect();
    }
  }

  /**
   * 连接到MQTT Broker
   */
  async connect(): Promise<void> {
    // 如果客户端为null但状态显示连接中，强制重置状态
    if (this.isConnecting && !this.client) {
      this.isConnecting = false;
      this.status.isConnecting = false;
      this.connectionPromise = null; // 清理可能存在的Promise
    }
    
    // 如果已经连接，直接返回
    if (this.isConnected()) {
      console.log('MqttManager: MQTT客户端已连接，跳过连接');
      return;
    }

    // 如果正在连接中，等待现有连接完成
    if (this.isConnecting && this.connectionPromise) {
      console.log('MqttManager: MQTT客户端正在连接中，等待连接完成...');
      return this.connectionPromise;
    }

    // 如果客户端存在但未连接，先清理
    if (this.client && !this.isConnected()) {
      console.warn('MqttManager: 清理未连接的客户端');
      try {
        this.client.end(true);
      } catch (e) {
        // 忽略清理错误
      }
      this.client = null;
    }

    this.isConnecting = true;
    this.status.isConnecting = true;

    this.connectionPromise = this.performConnect();

    try {
      await this.connectionPromise;
      // 连接成功后，再次验证连接状态
      if (!this.isConnected()) {
        throw new Error('MQTT连接完成但状态检查失败');
      }
      console.log('MqttManager: MQTT连接成功');
    } catch (error) {
      console.error('MqttManager: MQTT连接失败:', error);
      // 清理失败的连接
      if (this.client) {
        try {
          this.client.end(true);
        } catch (e) {
          // 忽略清理错误
        }
        this.client = null;
      }
      throw error;
    } finally {
      this.isConnecting = false;
      this.status.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  /**
   * WSS 失败时常见原因（浏览器对 TLS 很严格）
   */
  private logWssTroubleshootingHints(): void {
    const url = this.config.brokerUrl;
    if (!url.startsWith('wss:')) return;
    console.warn(
      'MqttManager [WSS 排查] 地址: ' +
        url +
        '\n' +
        '1) 证书与连接主机是否一致：页面用 HTTPS 域名访问，但 MQTT 连 wss://纯 IP 时，' +
        '若 TLS 证书是签给域名的，浏览器会拒绝。推荐：用 Nginx 在站点同域名下反代 /mqtt 到 EMQX WSS（见 .env.example）。\n' +
        '2) 防火墙 / 安全组放行 WSS 端口（如 8084）。\n' +
        '3) EMQX 已启用 WSS 监听（如 0.0.0.0:8084），path 与 URL 一致（常见 /mqtt）。\n' +
        '4) 服务器上可用 openssl s_client -connect <主机>:<端口> 检查 TLS 握手。'
    );
  }

  /**
   * 测试WebSocket连接（仅用于诊断）
   */
  private async testWebSocketConnection(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        console.log('MqttManager: 测试WebSocket连接:', url);
        const ws = new WebSocket(url);
        
        const timeout = setTimeout(() => {
          if (ws.readyState !== WebSocket.CLOSED) {
            ws.close();
          }
          const error = new Error('WebSocket连接测试超时（5秒）');
          console.error('MqttManager:', error.message);
          resolve(); // 不reject，因为这只是测试
        }, 5000);
        
        ws.onopen = () => {
          clearTimeout(timeout);
          console.log('MqttManager: ✅ WebSocket连接测试成功 - 服务器可达');
          ws.close(1000, '测试完成');
          resolve();
        };
        
        ws.onerror = (error) => {
          clearTimeout(timeout);
          const wsTarget = error?.target as WebSocket;
          
          console.error('MqttManager: ❌ WebSocket连接测试失败:', {
            error,
            type: error?.type,
            target: error?.target,
            readyState: wsTarget?.readyState,
            url,
            note: '关闭代码将在onclose事件中显示'
          });
          // 不reject，因为这只是测试，等待onclose事件获取关闭代码
        };
        
        ws.onclose = (event) => {
          clearTimeout(timeout);
          const closeCodeMessages: { [key: number]: string } = {
            1000: '正常关闭',
            1001: '端点离开（服务器关闭或浏览器导航）',
            1002: '协议错误',
            1003: '不支持的数据类型',
            1006: '异常关闭（无关闭帧）',
            1007: '数据格式错误',
            1008: '策略违规',
            1009: '消息过大',
            1010: '扩展协商失败',
            1011: '服务器内部错误',
            1012: '服务重启',
            1013: '服务过载',
            1014: '网关错误',
            1015: 'TLS握手失败'
          };
          
          if (event.code !== 1000) {
            console.error('MqttManager: ❌ WebSocket连接测试关闭:', {
              code: event.code,
              codeMessage: closeCodeMessages[event.code] || '未知关闭代码',
              reason: event.reason || '无原因',
              wasClean: event.wasClean,
              url,
              diagnosis: this.getWebSocketErrorCauses(event.code)
            });
          }
          resolve();
        };
      } catch (error) {
        console.error('MqttManager: WebSocket连接测试异常:', error);
        resolve(); // 不reject，因为这只是测试
      }
    });
  }
  
  /**
   * 获取WebSocket错误原因说明
   */
  private getWebSocketErrorCauses(closeCode?: number): string[] {
    const causes: string[] = [];
    
    if (!closeCode) {
      causes.push('无法获取关闭代码');
      return causes;
    }
    
    switch (closeCode) {
      case 1006:
        causes.push('连接异常关闭（最常见原因）');
        causes.push('1. 服务器端MQTT WebSocket服务未运行');
        causes.push('2. WebSocket路径不正确（当前: /mqtt）');
        causes.push('3. Nginx反向代理配置错误');
        causes.push('4. SSL/TLS证书问题');
        causes.push('5. 防火墙阻止连接');
        break;
      case 1002:
        causes.push('协议错误 - 检查MQTT协议版本配置');
        break;
      case 1008:
        causes.push('策略违规 - 可能是认证失败');
        causes.push('检查用户名和密码（JWT Token）是否正确');
        break;
      case 1011:
        causes.push('服务器内部错误 - 检查服务器端日志');
        break;
      case 1015:
        causes.push('TLS握手失败 - 检查SSL/TLS证书配置');
        break;
      default:
        causes.push(`关闭代码: ${closeCode}`);
        causes.push('请检查服务器端MQTT WebSocket服务状态');
    }
    
    return causes;
  }

  /**
   * 执行连接
   */
  private async performConnect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        // 构建连接选项
        const options: IClientOptions = {
          clientId: this.config.clientId,
          username: this.config.username,
          password: this.config.password,
          keepalive: this.config.keepalive,
          reconnectPeriod: this.config.reconnectPeriod,
          connectTimeout: this.config.connectTimeout,
          clean: this.config.clean,
          protocolVersion: 4, // MQTT 3.1.1
        };

        // 创建MQTT客户端
        console.log('MqttManager: 开始连接MQTT Broker:', this.config.brokerUrl);
        console.log('MqttManager: 连接选项:', {
          clientId: options.clientId,
          username: options.username,
          hasPassword: !!options.password,
          passwordLength: options.password ? String(options.password).length : 0,
          keepalive: options.keepalive,
          connectTimeout: options.connectTimeout,
          clean: options.clean,
          protocolVersion: options.protocolVersion
        });
        
        try {
          // 默认不再单独 new WebSocket 探测：会与 mqtt 重复连一次，且报错行号易误导（实际失败原因与 mqtt 相同）
          if (process.env.REACT_APP_MQTT_DIAGNOSTIC_WS === '1') {
            this.testWebSocketConnection(this.config.brokerUrl).catch(() => {});
          }

          this.client = mqtt.connect(this.config.brokerUrl, options);
          
          // 监听WebSocket级别的错误（如果可用，在setupEventListeners之前）
          if (this.client) {
            // 尝试多种方式获取stream
            const stream = (this.client as any).stream || (this.client as any).options?.stream;
            if (stream && stream.on) {
              stream.on('error', (wsError: any) => {
                console.error('MqttManager: WebSocket级别错误（详细）:', {
                  error: wsError,
                  type: typeof wsError,
                  constructor: wsError?.constructor?.name,
                  message: wsError?.message,
                  code: wsError?.code,
                  errno: wsError?.errno,
                  syscall: wsError?.syscall,
                  address: wsError?.address,
                  port: wsError?.port,
                  stack: wsError?.stack,
                  toString: wsError?.toString?.(),
                  stringified: JSON.stringify(wsError, Object.getOwnPropertyNames(wsError))
                });
              });
            }
          }
        } catch (connectError) {
          console.error('MqttManager: 创建MQTT客户端时发生异常:', {
            error: connectError,
            message: connectError instanceof Error ? connectError.message : String(connectError),
            stack: connectError instanceof Error ? connectError.stack : undefined
          });
          throw connectError;
        }

        // 设置连接超时
        let timeoutCleared = false;
        const connectTimeout = setTimeout(() => {
          if (!this.isConnected() && !timeoutCleared) {
            console.error('MqttManager: 连接超时');
            this.isConnecting = false;
            this.status.isConnecting = false;
            if (this.client) {
              try {
                this.client.end(true);
              } catch (e) {
                // 忽略清理错误
              }
              this.client = null;
            }
            timeoutCleared = true;
            reject(new Error('MQTT连接超时'));
          }
        }, this.config.connectTimeout || 30000);

        // 设置事件监听器        
        this.setupEventListeners(
          () => {
            if (!timeoutCleared) {
              clearTimeout(connectTimeout);
              timeoutCleared = true;
            }
            resolve();
          },
          (error) => {
            if (!timeoutCleared) {
              clearTimeout(connectTimeout);
              timeoutCleared = true;
            }
            reject(error);
          }
        );

      } catch (error) {
        console.error('MqttManager: 创建MQTT客户端失败', error);
        this.isConnecting = false;
        this.status.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * 设置事件监听器
   */
  private setupEventListeners(resolve: () => void, reject: (error: Error) => void): void {
    if (!this.client) {
      console.error('MqttManager: 客户端不存在，无法设置事件监听器');
      return;
    }

    // 连接成功
    this.client.on('connect', (packet) => {
      console.log('MqttManager: MQTT连接成功，连接包:', packet);
      this.status.isConnected = true;
      this.status.isConnecting = false;
      this.status.lastConnectedTime = Date.now();
      this.status.reconnectAttempts = 0;

      // 重新订阅之前的主题
      this.resubscribeTopics();

      this.callbacks.onConnect?.();
      resolve();
    });

    // 连接断开
    this.client.on('disconnect', (packet) => {
      this.status.isConnected = false;
      this.status.lastDisconnectedTime = Date.now();
      this.callbacks.onDisconnect?.('Disconnected');
    });

    // 重连
    this.client.on('reconnect', () => {
      this.status.reconnectAttempts++;
      this.callbacks.onReconnect?.();
    });

    // 离线
    this.client.on('offline', () => {
      console.warn('MqttManager: MQTT客户端离线');
      console.warn('MqttManager: 连接URL:', this.config.brokerUrl);
      console.warn('MqttManager: 连接配置:', {
        clientId: this.config.clientId,
        username: this.config.username,
        hasPassword: !!this.config.password
      });
      this.status.isConnected = false;
      this.callbacks.onOffline?.();
      
      // 如果正在连接中，离线事件表示连接失败
      if (this.isConnecting) {
        this.isConnecting = false;
        this.status.isConnecting = false;
        const error = new Error(
          `MQTT连接失败：客户端离线。` +
          `可能的原因：1) WebSocket服务器未运行或路径错误；2) 认证失败（用户名: ${this.config.username}）；` +
          `3) 网络连接问题。请检查服务器端MQTT WebSocket端点: ${this.config.brokerUrl}`
        );
        reject(error);
      }
    });

    // 连接关闭
    this.client.on('close', () => {
      console.warn('MqttManager: MQTT连接已关闭');
      console.warn('MqttManager: 连接URL:', this.config.brokerUrl);
      console.warn('MqttManager: 连接配置:', {
        clientId: this.config.clientId,
        username: this.config.username,
        hasPassword: !!this.config.password
      });
      this.status.isConnected = false;
      
      // 如果正在连接中，关闭事件表示连接失败
      if (this.isConnecting) {
        this.isConnecting = false;
        this.status.isConnecting = false;
        const error = new Error(
          `MQTT连接失败：连接已关闭。` +
          `可能的原因：1) WebSocket服务器拒绝连接；2) 认证失败（用户名: ${this.config.username}）；` +
          `3) 服务器端配置问题。请检查服务器端MQTT WebSocket端点: ${this.config.brokerUrl}`
        );
        reject(error);
      }
    });

    // 错误
    this.client.on('error', (error) => {
      // 尝试获取更详细的错误信息
      const errorDetails: any = {
        message: error?.message,
        code: (error as any)?.code,
        errno: (error as any)?.errno,
        syscall: (error as any)?.syscall,
        address: (error as any)?.address,
        port: (error as any)?.port,
        stack: error?.stack,
        name: error?.name,
        type: typeof error,
        constructor: error?.constructor?.name
      };
      
      // 尝试从error对象中提取所有属性
      if (error && typeof error === 'object') {
        try {
          Object.getOwnPropertyNames(error).forEach(prop => {
            if (!errorDetails[prop]) {
              errorDetails[prop] = (error as any)[prop];
            }
          });
        } catch (e) {
          // 忽略提取错误
        }
      }
      
      console.error('MqttManager: MQTT错误（完整信息）:', errorDetails);
      console.error('MqttManager: 原始错误对象:', error);

      const errMsg = String(errorDetails.message || error || '');
      const mqttConnackCode = (error as any)?.code;
      if (mqttConnackCode === 5 || /not authorized|connection refused: not authorized/i.test(errMsg)) {
        console.warn(
          'MqttManager [CONNACK 5 未授权] WebSocket 已通，但 EMQX 拒绝了 MQTT 用户名/密码。\n' +
            '前端规则：配置了 REACT_APP_MQTT_USERNAME 时，密码为 REACT_APP_MQTT_PASSWORD（勿留空）；未配置时用户名为登录名、密码为当前 JWT。\n' +
            '请在 EMQX「访问控制→认证/授权」中启用与之一致的认证方式；控制台登录账号不等于 MQTT 客户端账号。'
        );
      }

      if (this.config.brokerUrl.startsWith('wss:')) {
        this.logWssTroubleshootingHints();
      }

      this.status.isConnected = false;
      this.isConnecting = false;
      this.status.isConnecting = false;
      this.callbacks.onError?.(error);
      
      // 如果正在连接中，错误事件表示连接失败
      if (this.isConnecting) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : (errorDetails.message || String(error) || '未知错误');
        const enhancedError = new Error(
          `MQTT连接失败: ${errorMessage}。` +
          `连接URL: ${this.config.brokerUrl}，用户名: ${this.config.username}。` +
          `请检查：1) 服务器MQTT WebSocket服务是否运行；2) URL和路径是否正确；` +
          `3) 用户名和密码（JWT Token）是否正确；4) 网络连接和防火墙设置；` +
          `5) 服务器端MQTT认证配置。`
        );
        reject(enhancedError);
      }
    });

    // 收到消息
    this.client.on('message', (topic, message, packet) => {
      logDeviceStatusMqttRawFromBroker(topic, message);
      this.callbacks.onMessage?.(topic, message);
      this.messageListeners.forEach((listener) => {
        try {
          listener(topic, message);
        } catch (error) {
          console.error('MqttManager: 消息监听回调异常', error);
        }
      });
    });
  }

  /**
   * 重新订阅之前的主题
   */
  private resubscribeTopics(): void {
    if (!this.client || !this.isConnected()) return;

    for (const [topic, qos] of Array.from(this.subscriptions.entries())) {
      this.client.subscribe(topic, { qos: qos as any }, (error) => {
        if (error) {
          console.error(`MqttManager: 重新订阅主题失败 - ${topic}:`, error);
        }
      });
    }
  }

  /**
   * 订阅主题
   */
  async subscribe(topic: string, qos: 0 | 1 | 2 = 1): Promise<void> {
    // 如果未连接，尝试重新连接
    if (!this.isConnected()) {
      console.warn('MqttManager: MQTT客户端未连接，尝试重新连接...');
      try {
        await this.connect();
      } catch (error) {
        console.error('MqttManager: 重新连接失败:', error);
        throw new Error('MQTT客户端未连接且重新连接失败');
      }
    }

    return new Promise((resolve, reject) => {
      if (!this.client) {
        console.error('MqttManager: MQTT客户端不存在');
        reject(new Error('MQTT客户端不存在'));
        return;
      }
      
      if (!this.isConnected()) {
        console.error('MqttManager: MQTT客户端未连接');
        reject(new Error('MQTT客户端未连接'));
        return;
      }

      this.client.subscribe(topic, { qos }, (error) => {
        if (error) {
          console.error(`MqttManager: 订阅主题失败 - ${topic}:`, error);
          reject(error);
        } else {
          this.subscriptions.set(topic, qos);
          resolve();
        }
      });
    });
  }

  /**
   * 取消订阅主题
   */
  unsubscribe(topic: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.client || !this.isConnected()) {
        reject(new Error('MQTT客户端未连接'));
        return;
      }

      this.client.unsubscribe(topic, (error: any) => {
        if (error) {
          console.error(`MqttManager: 取消订阅主题失败 - ${topic}:`, error);
          reject(error);
        } else {
          this.subscriptions.delete(topic);
          resolve();
        }
      });
    });
  }

  /**
   * 发布消息
   */
  async publish(topic: string, message: string | Buffer, qos: 0 | 1 | 2 = 1, retain: boolean = false): Promise<void> {
    // 如果未连接，尝试重新连接
    if (!this.isConnected()) {
      console.warn('MqttManager: MQTT客户端未连接，尝试重新连接...');
      try {
        await this.connect();
      } catch (error) {
        console.error('MqttManager: 重新连接失败:', error);
        throw new Error('MQTT客户端未连接且重新连接失败');
      }
    }

    return new Promise((resolve, reject) => {
      if (!this.client || !this.isConnected()) {
        reject(new Error('MQTT客户端未连接'));
        return;
      }

      this.client.publish(topic, message, { qos, retain }, (error) => {
        if (error) {
          console.error(`MqttManager: 发布消息失败 - ${topic}:`, error);
          reject(error);
        } else {
          resolve();
        }
      });
    });
  }

  /**
   * 发布JSON对象
   */
  publishJson(topic: string, data: any, qos: 0 | 1 | 2 = 1, retain: boolean = false): Promise<void> {
    try {
      const jsonString = JSON.stringify(data);
      return this.publish(topic, jsonString, qos, retain);
    } catch (error) {
      console.error('MqttManager: JSON序列化失败', error);
      return Promise.reject(error);
    }
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<void> {
    if (!this.client) {
      return;
    }
    
    return new Promise((resolve) => {
      this.client!.end(false, {}, () => {
        this.status.isConnected = false;
        this.status.lastDisconnectedTime = Date.now();
        this.client = null;
        this.subscriptions.clear();
        this.messageListeners.clear();
        resolve();
      });
    });
  }

  /**
   * 设置事件回调
   */
  setCallbacks(callbacks: MqttEventCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 注册额外消息监听（与 setCallbacks 的 onMessage 并行）
   */
  addMessageListener(listener: (topic: string, message: string | Buffer) => void): () => void {
    this.messageListeners.add(listener);
    return () => {
      this.messageListeners.delete(listener);
    };
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    // 同时检查客户端状态和内部状态，确保一致性
    const clientConnected = this.client?.connected || false;
    const statusConnected = this.status.isConnected;
    
    // 如果状态不一致，以客户端状态为准
    if (clientConnected !== statusConnected) {
      console.warn(`MqttManager: 连接状态不一致 - 客户端: ${clientConnected}, 状态: ${statusConnected}`);
      this.status.isConnected = clientConnected;
    }
    
    return clientConnected;
  }

  /**
   * 获取连接状态
   */
  getStatus(): MqttConnectionStatus {
    return { ...this.status };
  }

  /**
   * 获取订阅的主题列表
   */
  getSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  /**
   * 检查是否已订阅指定主题
   */
  isSubscribed(topic: string): boolean {
    return this.subscriptions.has(topic);
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<MqttConnectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 获取配置
   */
  getConfig(): MqttConnectionConfig {
    return { ...this.config };
  }

  /**
   * 销毁管理器
   */
  async dispose(): Promise<void> {
    try {
      await this.disconnect();
    } catch (error) {
      console.error('MqttManager: 销毁失败', error);
      throw error;
    }
  }
}

// 创建单例实例
let mqttManagerInstance: MqttManager | null = null;

/**
 * 获取MQTT管理器实例
 */
export function getMqttManager(): MqttManager | null {
  return mqttManagerInstance;
}

/**
 * 创建MQTT管理器实例
 */
export function createMqttManager(config: MqttConnectionConfig): MqttManager {
  if (mqttManagerInstance) {
    return mqttManagerInstance;
  }

  mqttManagerInstance = new MqttManager(config);
  return mqttManagerInstance;
}

/**
 * 销毁MQTT管理器实例
 */
export async function destroyMqttManager(): Promise<void> {
  if (mqttManagerInstance) {
    await mqttManagerInstance.dispose();
    mqttManagerInstance = null;
  }
}